import { useCallback, useRef } from 'react';
import { useStreak } from '../contexts/StreakContext';
import { useAlertQueueActions } from '../contexts/AlertQueueContext';
import { useUser } from '@clerk/clerk-react';
import { queueResourceAlertsFromPayload } from '../utils/resourceAlerts';

// Session storage key for tracking shown streak alerts
const STREAK_ALERT_SHOWN_KEY = 'streak_alert_shown';

/**
 * Check if we've already shown the streak alert for a given completion.
 * Uses sessionStorage to persist within browser session.
 */
const hasShownStreakAlert = (gameSlug, gameDate) => {
  try {
    const shown = sessionStorage.getItem(STREAK_ALERT_SHOWN_KEY);
    if (!shown) return false;
    const shownSet = new Set(JSON.parse(shown));
    return shownSet.has(`${gameSlug}_${gameDate}`);
  } catch {
    return false;
  }
};

/**
 * Mark that we've shown the streak alert for a given completion.
 */
const markStreakAlertShown = (gameSlug, gameDate) => {
  try {
    const shown = sessionStorage.getItem(STREAK_ALERT_SHOWN_KEY);
    const shownSet = shown ? new Set(JSON.parse(shown)) : new Set();
    shownSet.add(`${gameSlug}_${gameDate}`);
    sessionStorage.setItem(STREAK_ALERT_SHOWN_KEY, JSON.stringify([...shownSet]));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Hook for games to handle streak updates from server responses.
 * 
 * This hook provides handleStreakFromResponse() which processes the streak info
 * returned by the gameplay service when a game completes. It:
 * - Updates the streak context with new data
 * - Queues resource alerts from payload (streak, stelline, learned_terms, gettoncini)
 * 
 * For anonymous users, it also updates the local streak optimistically.
 * 
 * Usage:
 * ```javascript
 * const { handleStreakFromResponse } = useGameStreak();
 * 
 * // In dispatchEvent when game completes:
 * if (response.app_resources?.streak) {
 *   handleStreakFromResponse(
 *     gameSlug,
 *     date,
 *     response.app_resources.streak,
 *     response.app_resources.gettoncini,
 *     response.app_resources.stelline,
 *     response.app_resources.learned_terms,
 *   );
 * }
 * ```
 */
export const useGameStreak = () => {
  const { updateStreakAndShowAlert, setStreakData } = useStreak();
  const { queueAlert } = useAlertQueueActions();
  const { isSignedIn } = useUser();
  
  // Track which games have already triggered alerts to prevent duplicates within component lifecycle
  const triggeredGamesRef = useRef(new Set());

  /**
   * Handle streak info received from the server after game completion.
   * 
   * @param {string} gameSlug - Game identifier
   * @param {string} gameDate - Puzzle date (YYYY-MM-DD)
   * @param {object} streakInfo - Streak info from server response
   * @param {number} streakInfo.current_streak - Current consecutive days
   * @param {number} streakInfo.longest_streak - Longest streak ever
   * @param {string|null} streakInfo.last_played_date - Last played date (YYYY-MM-DD)
   * @param {string|null} streakInfo.streak_start_date - Current streak start date
 * @param {boolean} streakInfo.streak_extended - Whether this completion extended the streak
 * @param {Array} streakInfo.streak_awards - Array of milestone awards granted
 * @param {object|null} gettonciniInfo - Gettoncini info from server response (for alerts)
 * @param {object|null} stellineInfo - Stelline info from server response (for alerts)
 * @param {object|null} learnedTermsInfo - Learned terms info from server response
 * @param {number} learnedTermsInfo.count - Total learned terms
 * @param {number} learnedTermsInfo.delta - Increment from this completion
 * @param {object} options - Optional alert behavior overrides
 * @param {number} options.initialDelayMs - Extra delay before the first queued alert
 * @param {number} options.firstAlertDelayMs - Absolute delay before the first queued alert
 * @param {boolean} options.skipStreakDataSync - Leave streak context updates to the caller
 * @param {object} options.payload - Original response, including reward occurrence metadata
 * @param {string[]} options.excludedResourceTypes - Resources already handled by celebrations
 */
  const handleStreakFromResponse = useCallback((gameSlug, gameDate, streakInfo, gettonciniInfo, stellineInfo = null, learnedTermsInfo = null, options = {}) => {
    const completionKey = `${gameSlug}_${gameDate}`;
    const initialDelayMs = Math.max(0, Number(options?.initialDelayMs) || 0);
    const firstAlertBaseDelay = Number.isFinite(Number(options?.firstAlertDelayMs))
      ? Math.max(0, Number(options.firstAlertDelayMs))
      : 2000 + initialDelayMs;
    const skipStreakDataSync = options?.skipStreakDataSync === true;
    
    // Prevent duplicate handling within same component lifecycle
    if (triggeredGamesRef.current.has(completionKey)) {
      console.warn(`Duplicate handleStreakFromResponse call prevented for ${completionKey}`);
      return;
    }
    triggeredGamesRef.current.add(completionKey);
    
    // Check if we've already shown alerts for this completion in this browser session
    const alreadyShown = hasShownStreakAlert(gameSlug, gameDate);
    
    // Update streak context with server data (preserve existing fields not in response)
    if (setStreakData && !skipStreakDataSync) {
      setStreakData(prev => ({
        ...prev,  // Preserve any existing fields
        currentStreak: streakInfo.current_streak || 0,
        longestStreak: streakInfo.longest_streak || 0,
        lastPlayedDate: streakInfo.last_played_date || null,
        streakStartDate: streakInfo.streak_start_date || null,
        // Use server-provided current week dates if available
        completedDatesCurrentWeek: Array.isArray(streakInfo.completed_dates_current_week)
          ? streakInfo.completed_dates_current_week
          : prev?.completedDatesCurrentWeek,
      }));
    }
    
    // For anonymous users, also update local streak (localStorage-based)
    if (!isSignedIn && !skipStreakDataSync) {
      updateStreakAndShowAlert();
    }
    
    // Don't show alerts if we've already shown them
    if (alreadyShown) {
      return;
    }
    
    // Mark that we're showing alerts for this completion
    markStreakAlertShown(gameSlug, gameDate);
    
    const responsePayload = options.payload || {
      app_resources: {
        gettoncini: gettonciniInfo || null,
        stelline: stellineInfo || null,
        learned_terms: learnedTermsInfo || null,
        streak: streakInfo || null,
      },
    };
    queueResourceAlertsFromPayload({
      queueAlert,
      payload: responsePayload,
      isAnonymous: !isSignedIn,
      firstAlertDelayMs: firstAlertBaseDelay,
      signedInDurationMs: 5000,
      anonymousDurationMs: 12000,
      excludedResourceTypes: options.excludedResourceTypes || [],
    });
  }, [setStreakData, updateStreakAndShowAlert, isSignedIn, queueAlert]);

  // Legacy function for backwards compatibility - now just logs a warning
  // This should not be called anymore; use handleStreakFromResponse instead
  const triggerStreakUpdate = useCallback((gameSlug, gameDate) => {
    console.warn(
      'triggerStreakUpdate is deprecated. Streak updates are now handled via handleStreakFromResponse from server event responses.'
    );
    return { streakData: null, isStreakExtended: false };
  }, []);

  return {
    handleStreakFromResponse,
    triggerStreakUpdate, // Kept for backwards compatibility, but deprecated
  };
}; 
